/**
 * useReportSnapshotHandler — React bridge for report snapshot orchestration.
 *
 * Responsibilities:
 *  - Manages the map of hidden container DOM refs (populated by ReportSnapshotViewportsHost).
 *  - Captures the whole template via ReportSnapshotHandlerService whenever the
 *    caller-supplied `captureSignature` changes, and tracks which snapshot IDs
 *    were produced by the template (vs manually taken by the user).
 *  - Subscribes to ViewportSnapshotService so both template and user snapshots are
 *    always up-to-date in React state.
 *  - Exposes templateSnapshots (ordered per config) and userSnapshots separately so
 *    ReportSnapshotPage can render them in distinct sections.
 *
 * Capture is driven entirely by `captureSignature`: a capture runs when it is
 * non-null and differs from the last captured one (replacing the prior template
 * snapshots), and is skipped when unchanged. This means snapshots are never taken
 * on unloaded data and refresh automatically when the underlying data changes —
 * with no manual trigger.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ReportSnapshotHandlerService } from './report-snapshot-handler-service';
import type { IReportSnapshotTemplate } from './types';

import { useVisualization } from '@/context';
import { SizingStateService } from '@/visualization/services/sizing/sizing-state-service';
import {
    ViewportSnapshotService,
    type ViewportSnapshot,
} from '@/visualization/services/viewport-snapshot-service';

export interface UseReportSnapshotHandlerResult {
    /**
     * Snapshots produced by the template, in config order.
     * Empty until the first capture pass completes.
     */
    templateSnapshots: ViewportSnapshot[];
    /**
     * Snapshots manually taken by the user via the snapshot panel.
     * Excludes any snapshot produced by the template.
     */
    userSnapshots: ViewportSnapshot[];
    /** True while a template capture sequence is in progress. */
    isCapturing: boolean;
    /** Set if the last capture pass threw an unexpected error. */
    error: Error | null;
    /**
     * Ref-registration callback for ReportSnapshotViewportsHost.
     * Pass this as the `registerRef` prop.
     */
    registerRef: (id: string, el: HTMLDivElement | null) => void;
    /** Toggle a snapshot's includedInReport flag (hides/shows it in the report). */
    toggleReportInclusion: (id: string) => void;
}

/** Debounce window so a burst of measurement/keypoint updates triggers one capture. */
const CAPTURE_DEBOUNCE_MS = 300;

/**
 * @param template        Captures to produce.
 * @param captureSignature A string that changes whenever the data the snapshots
 *   depend on changes (e.g. EVAR measurements), or `null` while that data is not
 *   ready. The handler (re)captures whenever the signature becomes non-null and
 *   differs from the last captured one, and skips capture when it is unchanged —
 *   so snapshots are never taken on blank/unloaded data and refresh automatically
 *   when the data changes.
 */
export function useReportSnapshotHandler(
    template: IReportSnapshotTemplate,
    captureSignature: string | null
): UseReportSnapshotHandlerResult {
    const { getViewportService, getServiceManager } = useVisualization();

    const [allSnapshots, setAllSnapshots] = useState<ViewportSnapshot[]>([]);
    const [isCapturing, setIsCapturing] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    // Incremented every time a container ref registers/unregisters so the
    // capture effect re-evaluates once all hidden viewports have mounted.
    const [containersReadyCount, setContainersReadyCount] = useState(0);
    // Bumped after each capture pass completes so the capture effect re-evaluates
    // (handles a signature that changed while a capture was already running).
    const [recaptureTick, setRecaptureTick] = useState(0);

    // Ordered list of snapshot IDs that belong to the current template run.
    // Maintained as a ref so captureAll can update it without triggering re-renders.
    const templateSnapshotIdsRef = useRef<string[]>([]);

    // Map from config.id → container <div> populated by ReportSnapshotViewportsHost
    const containerRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    // Signature of the data the current template snapshots were captured from.
    // A capture runs only when the incoming signature differs from this.
    const lastCapturedSignatureRef = useRef<string | null>(null);
    // Guards against overlapping capture passes.
    const isCapturingRef = useRef(false);

    // Subscribe to ViewportSnapshotService to keep allSnapshots in sync
    useEffect(() => {
        const snapshotService = getServiceManager().getService<ViewportSnapshotService>(
            ViewportSnapshotService.SERVICE_NAME
        );

        const sync = () => setAllSnapshots(snapshotService.getAllSnapshots());

        // Hydrate on mount
        sync();

        const unsubStored = snapshotService.subscribe(snapshotService.EVENTS.SNAPSHOT_STORED, sync);
        const unsubRemoved = snapshotService.subscribe(snapshotService.EVENTS.SNAPSHOT_REMOVED, sync);
        const unsubCleared = snapshotService.subscribe(snapshotService.EVENTS.SNAPSHOTS_CLEARED, () => {
            templateSnapshotIdsRef.current = [];
            lastCapturedSignatureRef.current = null;
            setAllSnapshots([]);
        });
        const unsubUpdated = snapshotService.subscribe(snapshotService.EVENTS.SNAPSHOT_UPDATED, sync);

        return () => {
            unsubStored();
            unsubRemoved();
            unsubCleared();
            unsubUpdated();
        };
    }, [getServiceManager]);

    const registerRef = useCallback((id: string, el: HTMLDivElement | null) => {
        if (el) {
            containerRefs.current.set(id, el);
        } else {
            containerRefs.current.delete(id);
        }
        setContainersReadyCount(containerRefs.current.size);
    }, []);

    const captureAll = useCallback(
        async (signature: string | null) => {
            if (isCapturingRef.current) return;
            isCapturingRef.current = true;
            setIsCapturing(true);
            setError(null);

            try {
                const serviceManager = getServiceManager();

                const snapshotService = serviceManager.getService<ViewportSnapshotService>(
                    ViewportSnapshotService.SERVICE_NAME
                );
                const sizingStateService = serviceManager.getService<SizingStateService>(
                    SizingStateService.SERVICE_NAME
                );

                // Remove previous template snapshots from the store before re-capturing
                for (const id of templateSnapshotIdsRef.current) {
                    snapshotService.removeSnapshot(id);
                }

                const service = new ReportSnapshotHandlerService(
                    getViewportService(),
                    snapshotService,
                    sizingStateService
                );

                const configIdToSnapshotId = await service.captureAll(template, containerRefs.current);

                // Preserve config order for templateSnapshots
                const orderedIds: string[] = [];
                for (const config of template.configs) {
                    const snapshotId = configIdToSnapshotId.get(config.id);
                    if (snapshotId) orderedIds.push(snapshotId);
                }
                templateSnapshotIdsRef.current = orderedIds;
                snapshotService.recordTemplateCaptureResult(template.id, orderedIds, signature);
                lastCapturedSignatureRef.current = signature;

                // Re-sync state (captureAll already triggered SNAPSHOT_STORED events,
                // but setting the ref requires a manual re-sync to recompute derived state)
                setAllSnapshots(snapshotService.getAllSnapshots());
            } catch (err) {
                const wrappedError = err instanceof Error ? err : new Error('Snapshot capture failed');
                console.error('[useReportSnapshotHandler] captureAll failed:', wrappedError);
                setError(wrappedError);
            } finally {
                isCapturingRef.current = false;
                setIsCapturing(false);
                // Re-evaluate the capture effect in case the signature changed mid-capture.
                setRecaptureTick((t) => t + 1);
            }
        },
        [template, getViewportService, getServiceManager]
    );

    // On remount, restore a prior capture result from the service so the capture
    // effect below is skipped when up-to-date snapshots already exist.
    useEffect(() => {
        const snapshotService = getServiceManager().getService<ViewportSnapshotService>(
            ViewportSnapshotService.SERVICE_NAME
        );
        const prior = snapshotService.getTemplateCaptureResult(template.id);
        if (!prior) return;

        // Adopt the ids still present in the store even when the set is incomplete:
        // captureAll() removes exactly these before re-capturing, so a leftover from
        // a previous pass can never survive as a duplicate (which is what happened
        // when this bailed out on a partially present set). An incomplete set also
        // leaves the signature null, forcing a re-capture so the report is whole.
        const present = prior.ids.filter((id) => snapshotService.getSnapshot(id) !== undefined);
        templateSnapshotIdsRef.current = present;
        lastCapturedSignatureRef.current = present.length === prior.ids.length ? prior.signature : null;
        setAllSnapshots(snapshotService.getAllSnapshots());
    }, [template, getServiceManager]);

    // Capture (or re-capture) whenever the data signature changes. A null
    // signature means the data is not ready, so no capture runs on blank data;
    // an unchanged signature is a no-op, so identical data is never re-captured.
    useEffect(() => {
        if (captureSignature === null) return;
        if (template.configs.length === 0) return;
        if (containerRefs.current.size < template.configs.length) return;
        if (captureSignature === lastCapturedSignatureRef.current) return;
        if (isCapturingRef.current) return; // retried after the current pass via recaptureTick

        const handle = setTimeout(() => void captureAll(captureSignature), CAPTURE_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [captureSignature, containersReadyCount, recaptureTick, template, captureAll]);

    const toggleReportInclusion = useCallback(
        (id: string) => {
            const snapshotService = getServiceManager().getService<ViewportSnapshotService>(
                ViewportSnapshotService.SERVICE_NAME
            );
            snapshotService.toggleReportInclusion(id);
        },
        [getServiceManager]
    );

    // Derive templateSnapshots (ordered) and userSnapshots from allSnapshots + templateIds ref.
    // Every ref mutation is paired with a setAllSnapshots() call, so keying the memo
    // on allSnapshots is enough to keep the derived lists in sync.
    const { templateSnapshots, userSnapshots } = useMemo(() => {
        const templateIds = templateSnapshotIdsRef.current;
        const templateIdSet = new Set(templateIds);

        const byId = new Map(allSnapshots.map((s) => [s.id, s]));
        const template = templateIds
            .map((id) => byId.get(id))
            .filter((s): s is ViewportSnapshot => s !== undefined && s.includedInReport);
        const user = allSnapshots.filter((s) => !templateIdSet.has(s.id) && s.includedInReport);

        return { templateSnapshots: template, userSnapshots: user };
    }, [allSnapshots]);

    return {
        templateSnapshots,
        userSnapshots,
        isCapturing,
        error,
        registerRef,
        toggleReportInclusion,
    };
}
